import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, ModalBody, ModalHeader, Col, Row, ModalFooter, Form } from 'reactstrap';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import FormInput from '@/components/forms/FormInput';
import { UserFilter } from '@/services/users/user-filters';
import { UserRoles } from '@/services/users/user-types';

interface UserFilterFormProps {
  onClose: () => void;
  onApplyFilters: (filters: UserFilter) => void;
  currentFilters: UserFilter;
}

const calculateRole = (isAgent: boolean | undefined, isAmbassador: boolean | undefined) => {
  if (isAgent === undefined && isAmbassador === undefined) {
    return undefined;
  }
  if (isAmbassador === true && isAgent === false) {
    return UserRoles.AMBASSADOR;
  } else if (isAgent === true && isAmbassador === false) {
    return UserRoles.AGENT;
  } else if (isAgent === false && isAmbassador === false) {
    return UserRoles.NO_ROLE;
  } else return undefined;
};
const UserFilterForm: React.FC<UserFilterFormProps> = ({ onClose, onApplyFilters, currentFilters }) => {
  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      ...currentFilters,
      role: calculateRole(currentFilters.isAgent, currentFilters.isAmbassador),
    },
  });

  const role = watch('role');

  useEffect(() => {
    switch (role) {
      case UserRoles.AGENT:
        setValue('isAgent', true);
        setValue('isAmbassador', false);
        break;
      case UserRoles.AMBASSADOR:
        setValue('isAgent', false);
        setValue('isAmbassador', true);
        break;
      case UserRoles.NO_ROLE:
        setValue('isAgent', false);
        setValue('isAmbassador', false);
        break;
      default:
        setValue('isAgent', undefined);
        setValue('isAmbassador', undefined);
        break;
    }
  }, [role, setValue]);

  const onSubmit = (data: any) => {
    const { role, ...rest } = data;
    const cleanedFilters = Object.entries(rest).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as UserFilter);

    onApplyFilters(cleanedFilters);
  };

  return (
    <Modal centered isOpen>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader toggle={onClose} className="d-flex justify-content-between align-items-center w-full">
          Filters
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col xs={12}>
              <FormInput
                label="Role"
                name="role"
                control={control}
                type="select"
                options={[
                  { label: 'No Role', value: UserRoles.NO_ROLE },
                  { label: 'Agent', value: UserRoles.AGENT },
                  { label: 'Ambassador', value: UserRoles.AMBASSADOR },
                ]}
              />
            </Col>
            <Col xs={12}>
              <FormInput
                label="Alias"
                name="alias"
                control={control}
                type="text"
                placeholder="Search by user alias..."
              />
            </Col>
            <Col xs={12}>
              <FormInput
                label="Referral Code"
                name="referralCode"
                control={control}
                type="text"
                placeholder="Search by referral code..."
              />
            </Col>
            <Col xs={12}>
              <FormInput
                label="Smart Wallet"
                name="smartWalletAddress"
                control={control}
                type="text"
                placeholder="Search by smart wallet..."
              />
            </Col>
            <Col xs={12}>
              <FormInput
                label="Main Wallet"
                name="mainWalletAddress"
                control={control}
                type="text"
                placeholder="Search by main wallet..."
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-outline"
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            variant={ButtonVariants.primary}
            size={ButtonSizes.sm}
            onClick={handleSubmit(onSubmit)}
          >
            Apply Filter
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default UserFilterForm;
